import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';

import BrandHeader from '../../../components/common/BrandHeader';
import DetailRow from '../../../components/common/DetailRow';
import NextSteps from '../../../components/common/NextSteps';
import OutlineButton from '../../../components/common/OutlineButton';
import ResultHero from '../../../components/common/ResultHero';
import {
  Card,
  CardBody,
  HelpText,
  PageShell,
  SectionTitle,
} from '../../../components/common/Card';
import { CONTACT_PHONE } from '../../../constants/business';
import { BOOKED_NEXT_STEPS } from '../../../constants/nextSteps';
import {
  formatAppointmentDate,
  formatAppointmentTime,
  formatAppointmentTypeLabel,
} from '../../../utils/appointmentDisplayUtils';
import {
  buildGoogleCalendarUrl,
  generateCalendarEvent,
} from '../../../utils/calendarEventUtils';

// Shown on the booking link once the patient already has a booked
// appointment, so they see it instead of booking again.
const BookedAppointment = () => {
  const workflow = useSelector(({ workflow }) => workflow.data);
  const { specialist, appointment } = workflow;

  const clinicianName = specialist
    ? `Dr. ${specialist.firstName} ${specialist.lastName}`
    : '';
  const clinicianDetail = specialist?.speciality
    ? `${clinicianName} · ${specialist.speciality}`
    : clinicianName;
  const appointmentDate = formatAppointmentDate(appointment?.startDateTime);
  const appointmentTime = formatAppointmentTime(appointment?.startDateTime);
  const appointmentTypeLabel = formatAppointmentTypeLabel(
    appointment?.appointmentType
  );
  const calendarUrl = useMemo(
    () => buildGoogleCalendarUrl(generateCalendarEvent(workflow)),
    [workflow]
  );

  return (
    <>
      <BrandHeader />
      <PageShell>
        <ResultHero
          tone="success"
          title="You already have an appointment booked"
          subtitle={
            clinicianName
              ? `Your appointment with ${clinicianName} is booked. You don't need to book again.`
              : "Your appointment is booked. You don't need to book again."
          }
        />

        <Card>
          <CardBody>
            <SectionTitle>Your appointment</SectionTitle>
            {clinicianDetail && (
              <DetailRow label="Clinician" value={clinicianDetail} />
            )}
            {appointmentDate && (
              <DetailRow label="Date" value={appointmentDate} />
            )}
            {appointmentTime && (
              <DetailRow label="Time" value={appointmentTime} />
            )}
            {appointmentTypeLabel && (
              <DetailRow label="Type" value={appointmentTypeLabel} />
            )}
          </CardBody>
        </Card>

        <Card>
          <CardBody>
            <SectionTitle>What happens next</SectionTitle>
            <NextSteps steps={BOOKED_NEXT_STEPS} />
          </CardBody>
        </Card>

        {calendarUrl && (
          <Card>
            <CardBody>
              <OutlineButton
                href={calendarUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                Add to calendar
              </OutlineButton>
            </CardBody>
          </Card>
        )}

        <HelpText>
          Need to change or cancel your appointment? Press the chat icon or call{' '}
          <b>{CONTACT_PHONE}</b> to speak with us.
        </HelpText>
      </PageShell>
    </>
  );
};

export default BookedAppointment;
